import i18n from '@/i18n';
/**
 * 商品库按入库批次聚合，以及单条商品 → 第三方任务详情同款 SPU 表格数据。
 */

import { stripImageFileExtension } from '@/utils/imageUrlUtils';
import {
  extractColorCodeFromPatternName,
  extractColorFromImageNames,
} from './listingPatternColorMatch';
import {
  carouselUrlsForListingItem,
  detailImageUrlsForListingItem,
  isGenericListingImageName,
  isOpaqueStorageBaseName,
  printImageNamesForListingItem,
  printImageUrlsForListingItem,
  resolveListingItemSourceLabel,
} from './platformListingItemMedia';
import { resolveListingItemDisplayTitle, getListingPreferredOverrides } from './preferDownstreamListingAssets';
import {
  LISTING_PUBLISH_COLOR_INDEXES_FIELD,
  resolveListingColorSlots,
  detailUrlsFromListingColorSlot,
} from './platformListingProductMeta';
import { resolveListingStatus } from './syncListingProductMetaFromJobs';

export const LISTING_BATCH_QUERY_KEY = 'listingBatchKey';
export const LISTING_ITEM_QUERY_KEY = 'listingItemId';
export const LISTING_VIEW_QUERY_KEY = 'listingView';
/** 入库完成后自动打开 SPU 复色弹窗（其它模块带入 → 复色 时挂在 URL 上） */
export const LISTING_RECOLOR_QUERY_KEY = 'listingRecolor';

export function readListingBatchKeyFromSearch(search) {
  return String(new URLSearchParams(search || '').get(LISTING_BATCH_QUERY_KEY) || '').trim();
}

export function readListingRecolorFromSearch(search) {
  return String(new URLSearchParams(search || '').get(LISTING_RECOLOR_QUERY_KEY) || '').trim() === '1';
}

export function readListingItemIdFromSearch(search) {
  return String(new URLSearchParams(search || '').get(LISTING_ITEM_QUERY_KEY) || '').trim();
}

export function readListingAutoViewFromSearch(search) {
  return String(new URLSearchParams(search || '').get(LISTING_VIEW_QUERY_KEY) || '').trim() === 'auto';
}

export function fileNameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw, 'https://local.invalid');
    const key = parsed.searchParams.get('key') || parsed.searchParams.get('objectKey') || '';
    if (key) {
      const fromKey = decodeURIComponent((key.split('/').filter(Boolean).pop() || '').split('?')[0]);
      if (fromKey && fromKey !== 'view') return fromKey;
    }
    const base = decodeURIComponent((parsed.pathname.split('/').filter(Boolean).pop() || '').split('?')[0]);
    if (base && base !== 'view') return base;
    return '';
  } catch {
    const cleaned = raw.split('?')[0];
    const parts = cleaned.split('/').filter(Boolean);
    try {
      return decodeURIComponent(parts[parts.length - 1] || '');
    } catch {
      return parts[parts.length - 1] || '';
    }
  }
}

export function toNamedImageList(urls, names, fallbackPrefix) {
  const list = Array.isArray(urls) ? urls : [];
  const nameList = Array.isArray(names) ? names : [];
  return list.map((url, index) => {
    const rawUrl = String(url || '').trim();
    const explicit = String(nameList[index] || '').trim();
    const fromUrl = fileNameFromUrl(rawUrl);
    const usableExplicit = explicit && !isGenericListingImageName(explicit) ? explicit : '';
    const usableUrl = fromUrl
      && !isGenericListingImageName(fromUrl)
      && !isOpaqueStorageBaseName(fromUrl)
      ? fromUrl
      : '';
    const rawName = usableExplicit || usableUrl || `${fallbackPrefix}${index + 1}`;
    return {
      url: rawUrl,
      name: stripImageFileExtension(rawName) || rawName,
      fileName: rawName,
      index,
    };
  }).filter((img) => img.url);
}

export function resolveListingBatchKey(item) {
  const fromRow = String(item?.batchKey || item?.batch_key || '').trim();
  if (fromRow) return fromRow;
  const id = item?.id;
  return id != null && String(id).trim() ? `item:${id}` : '';
}

export function collectListingBatchPreviewUrls(items) {
  const urls = [];
  const push = (list) => {
    for (const url of list || []) {
      const raw = String(url || '').trim();
      if (!raw || urls.includes(raw)) continue;
      urls.push(raw);
      if (urls.length >= 4) return true;
    }
    return false;
  };
  const rows = Array.isArray(items) ? items : [];
  for (const item of rows) {
    push(printImageUrlsForListingItem(item));
  }
  if (urls.length) return urls;
  for (const item of rows) {
    push(carouselUrlsForListingItem(item));
  }
  if (urls.length) return urls;
  for (const item of rows) {
    push(detailImageUrlsForListingItem(item));
  }
  return urls;
}

export function resolveListingBatchSourceLabel(items) {
  const first = (Array.isArray(items) ? items : [])[0];
  if (!first) return i18n.t('workbench:listingPublish.sourceDetailBridge');
  return resolveListingItemSourceLabel(first);
}

export function formatListingDetailTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return String(value);
  }
}

/**
 * 批次数量按 SPU（商品库条目）计，不按图片张数。
 * @param {{ items?: object[], itemCount?: number, expectedItemCount?: number, ingestPending?: boolean, ingestProgress?: { done?: number, total?: number } }} batch
 */
export function countListingBatchSpus(batch) {
  const items = Array.isArray(batch?.items) ? batch.items : [];
  if (items.length > 0) {
    const ids = new Set(
      items.map((row) => String(row?.id ?? '').trim()).filter(Boolean),
    );
    return ids.size;
  }
  if (batch?.ingestPending) {
    const done = Number(batch?.ingestProgress?.done ?? batch?.itemCount ?? 0) || 0;
    if (done > 0) return done;
  }
  const expected = Number(batch?.expectedItemCount ?? 0);
  if (expected > 0) return expected;
  return Number(batch?.itemCount ?? 0) || 0;
}

/**
 * 批次内已刊登 SPU 数（本地 meta + 服务端 targetShopId）。
 * 不依赖自动化进度字段，避免「详情已刊登、列表仍为 0」。
 * @param {{ items?: object[] }} batch
 */
export function countListedListingBatchSpus(batch) {
  const items = Array.isArray(batch?.items) ? batch.items : [];
  if (items.length === 0) return 0;
  const listedIds = new Set();
  items.forEach((row) => {
    if (resolveListingStatus(row) !== 'listed') return;
    const id = String(row?.id ?? '').trim();
    if (id) listedIds.add(id);
  });
  return listedIds.size;
}

/**
 * 待上品条目按 batchKey 聚合成商品库批次。
 * 无 batchKey 的历史数据各自成批，避免混进其它入库。
 * @param {object[]} items
 * @returns {object[]}
 */
export function groupListingItemsByBatch(items) {
  const list = Array.isArray(items) ? items : [];
  const map = new Map();
  const order = [];
  list.forEach((item) => {
    const key = resolveListingBatchKey(item);
    if (!key) return;
    if (!map.has(key)) {
      map.set(key, {
        batchKey: key,
        items: [],
        createdAt: item?.createdAt || item?.created_at || '',
        ownerUserId: item?.ownerUserId ?? null,
      });
      order.push(key);
    }
    const batch = map.get(key);
    batch.items.push(item);
    const created = item?.createdAt || item?.created_at || '';
    if (created && (!batch.createdAt || new Date(created) < new Date(batch.createdAt))) {
      batch.createdAt = created;
    }
    if (batch.ownerUserId == null && item?.ownerUserId != null) {
      batch.ownerUserId = item.ownerUserId;
    }
  });
  return order
    .map((key) => {
      const batch = map.get(key);
      return {
        ...batch,
        itemCount: countListingBatchSpus(batch),
        previewImageUrls: collectListingBatchPreviewUrls(batch.items),
        sourceLabel: resolveListingBatchSourceLabel(batch.items),
      };
    })
    .sort((a, b) => {
      const tb = new Date(b.createdAt || 0).getTime();
      const ta = new Date(a.createdAt || 0).getTime();
      if (Number.isFinite(tb) && Number.isFinite(ta) && tb !== ta) return tb - ta;
      return String(b.batchKey).localeCompare(String(a.batchKey));
    });
}

function listingColorSlots(item) {
  return resolveListingColorSlots(item);
}

function resolveListingMainImageNames(item) {
  const preferred = getListingPreferredOverrides(item?.id);
  if (Array.isArray(preferred?.mainImageNames) && preferred.mainImageNames.length) {
    return preferred.mainImageNames;
  }
  return Array.isArray(item?.mainImageNames) ? item.mainImageNames : [];
}

function resolveListingDetailImageNames(item, slotIndex = 0) {
  const slots = listingColorSlots(item);
  if (slots.length > 0) {
    const slot = slots[slotIndex] ?? slots[0];
    const detailNames = Array.isArray(slot?.detailNames)
      ? slot.detailNames
      : (Array.isArray(slot?.detail_names) ? slot.detail_names : []);
    if (detailNames.length) return detailNames;
  }
  const preferred = getListingPreferredOverrides(item?.id);
  if (Array.isArray(preferred?.detailImageNames) && preferred.detailImageNames.length) {
    return preferred.detailImageNames;
  }
  return Array.isArray(item?.detailImageNames) ? item.detailImageNames : [];
}

/**
 * 商品库单条 → 第三方任务详情同款 SPU 分组（印花跨 SKC，颜色行拆主图/细节图）。
 * @param {object} item
 */
export function listingItemToSpuGroup(item) {
  if (!item) return null;
  const printUrls = printImageUrlsForListingItem(item);
  const printNames = printImageNamesForListingItem(item);
  const printImages = toNamedImageList(printUrls, printNames, '印花');
  const titleText = String(
    resolveListingItemDisplayTitle(item) || item?.title || '',
  ).trim();
  const slots = listingColorSlots(item);

  let skcRows;
  if (slots.length > 0) {
    skcRows = slots.map((slot, slotIndex) => {
      const mainUrl = String(slot?.mainUrl || slot?.main_url || '').trim();
      const mainName = String(slot?.mainName || slot?.patternName || slot?.main_name || '').trim();
      const detailUrls = detailUrlsFromListingColorSlot(slot);
      const detailNames = resolveListingDetailImageNames(item, slotIndex);
      const color = {
        colorLabel: String(slot?.colorLabel || slot?.color_label || '').trim(),
        colorCode: String(slot?.colorCode || slot?.color_code || '').trim(),
      };
      const nameSources = [
        mainName,
        ...detailNames,
        ...detailUrls.map((url) => fileNameFromUrl(url)),
      ];
      if (!color.colorLabel || !color.colorCode) {
        const fromNames = extractColorFromImageNames(nameSources);
        if (!color.colorLabel) color.colorLabel = fromNames.colorLabel;
        if (!color.colorCode) {
          color.colorCode = fromNames.colorCode || extractColorCodeFromPatternName(mainName);
        }
      }
      return {
        item,
        slotIndex,
        compositeImages: mainUrl ? toNamedImageList([mainUrl], [mainName], '主图') : [],
        detailImages: toNamedImageList(detailUrls, detailNames, '细节'),
        color,
      };
    });
  } else {
    const mainUrls = carouselUrlsForListingItem(item).slice(0, 1);
    const detailUrls = detailImageUrlsForListingItem(item);
    const mainImages = toNamedImageList(mainUrls, resolveListingMainImageNames(item), '主图');
    const detailImages = toNamedImageList(detailUrls, resolveListingDetailImageNames(item), '细节');
    const preferred = getListingPreferredOverrides(item?.id);
    const color = {
      colorLabel: String(preferred?.colorLabel || '').trim(),
      colorCode: String(preferred?.colorCode || '').trim(),
    };
    if (!color.colorLabel && !color.colorCode) {
      Object.assign(color, extractColorFromImageNames([
        ...printImages,
        ...mainImages,
        ...detailImages,
      ]));
    } else if (!color.colorCode) {
      const fromNames = extractColorFromImageNames([...mainImages, ...detailImages]);
      if (fromNames.colorCode) color.colorCode = fromNames.colorCode;
    }
    skcRows = [{
      item,
      slotIndex: 0,
      compositeImages: mainImages,
      detailImages,
      color,
    }];
  }

  return {
    spuKey: String(item.id ?? ''),
    titleText,
    items: [item],
    printImages,
    printItem: item,
    skcRows,
  };
}

export function buildListingImageKey(itemId, kind, slotIndex, imageIndex) {
  return `${kind}:${itemId}:${slotIndex}:${imageIndex}`;
}

export function parseListingImageKey(key) {
  const match = String(key || '').match(/^(print|composite|detail):(.+):(\d+):(\d+)$/);
  if (!match) return null;
  return {
    kind: match[1],
    itemId: match[2],
    slotIndex: Number(match[3]),
    index: Number(match[4]),
  };
}

export function collectListingGroupImageKeys(group) {
  const itemId = String(group?.printItem?.id ?? group?.items?.[0]?.id ?? '');
  if (!itemId) return [];
  const keys = [];
  (group?.printImages || []).forEach((img) => {
    if (!img?.url) return;
    keys.push(buildListingImageKey(itemId, 'print', 0, img.index));
  });
  (group?.skcRows || []).forEach((row) => {
    const slotIndex = Number(row?.slotIndex) || 0;
    (row?.compositeImages || []).forEach((img) => {
      if (!img?.url) return;
      keys.push(buildListingImageKey(itemId, 'composite', slotIndex, img.index));
    });
    (row?.detailImages || []).forEach((img) => {
      if (!img?.url) return;
      keys.push(buildListingImageKey(itemId, 'detail', slotIndex, img.index));
    });
  });
  return keys;
}

/**
 * 按「勾选的颜色主图」决定每个 SPU 本批实际刊登哪些颜色。
 * 颜色行的主图（composite 图）勾选即代表该颜色要刊登：
 * - 该 SPU 一个颜色主图都没勾 → 不限制，保持全色刊登
 * - 只勾了部分颜色主图 → 只刊登这些颜色（挂 ipePublishColorIndexes）
 * - 全都勾上 / 单色 SPU → 不限制
 * @param {object[]} rows 待刊登的商品行（一行一个 SPU）
 * @param {Iterable<string>} imageKeys 当前勾选的图片 key
 */
export function applyListingSelectedColorScope(rows, imageKeys) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (list.length === 0) return list;
  const pickedByItem = new Map();
  (imageKeys ? Array.from(imageKeys) : []).forEach((key) => {
    const parsed = parseListingImageKey(key);
    if (!parsed || parsed.kind !== 'composite') return;
    const bucket = pickedByItem.get(String(parsed.itemId)) || new Set();
    bucket.add(parsed.slotIndex);
    pickedByItem.set(String(parsed.itemId), bucket);
  });
  if (pickedByItem.size === 0) return list;
  return list.map((row) => {
    const bucket = pickedByItem.get(String(row?.id));
    if (!bucket || bucket.size === 0) return row;
    const slots = resolveListingColorSlots(row);
    const total = Array.isArray(slots) ? slots.length : 0;
    if (total <= 1) return row;
    const indexes = [...bucket]
      .filter((index) => Number.isInteger(index) && index >= 0 && index < total)
      .sort((a, b) => a - b);
    if (indexes.length === 0 || indexes.length >= total) return row;
    return { ...row, [LISTING_PUBLISH_COLOR_INDEXES_FIELD]: indexes };
  });
}

export function primaryMainImageFromListingGroup(group) {
  const rows = Array.isArray(group?.skcRows) ? group.skcRows : [];
  for (const row of rows) {
    const img = (row?.compositeImages || [])[0];
    const url = String(img?.url || '').trim();
    if (url) return { url, name: img?.name || '主图' };
  }
  const print = (group?.printImages || [])[0];
  const printUrl = String(print?.url || '').trim();
  if (printUrl) return { url: printUrl, name: print?.name || '印花图' };
  return null;
}
